import * as React from "react";
import ChapterHeader from "./chapter-header";
import AccountHeader from "./account-header";
import AccountYearEditor from "./account-year-editor";
import Operations from "../common/operations";
import * as Model from "../../../model";
import * as LoanDomain from "../../../domain/sfp/loan-domain";

const amountCellProps: CellProps = {
    format: "0,0",
    language: "fr-FR",
    type: "numeric",
    mantissa: 0,
};

const orderNumbers = ["68*", "78*", "675*", "676*", "775*", "776*", "777*"];

// Observation map cache
let _obsMapCache: Map<string, Model.Observation> = null;
let _obsMapLa: any = null;

function _getObsMap(la: Model.LocalAuthority): Map<string, Model.Observation> {
    if (_obsMapLa === la && _obsMapCache) return _obsMapCache;
    _obsMapCache = new Map();
    if (la.observations) {
        for (const o of la.observations) {
            const key = `${o.year}-${o.account.id}`;
            if (!_obsMapCache.has(key)) {
                _obsMapCache.set(key, o);
            }
        }
    }
    _obsMapLa = la;
    return _obsMapCache;
}

// Observation map with receipts for dispense functions
let _obsMapReceiptsCache: Map<string, Model.Observation> = null;
let _obsMapReceiptsLa: any = null;

function _getObsMapReceipts(la: Model.LocalAuthority): Map<string, Model.Observation> {
    if (_obsMapReceiptsLa === la && _obsMapReceiptsCache) return _obsMapReceiptsCache;
    _obsMapReceiptsCache = new Map();
    if (la.observations) {
        for (const o of la.observations) {
            const key = `${o.year}-${o.account.id}-${o.receipts}`;
            if (!_obsMapReceiptsCache.has(key)) {
                _obsMapReceiptsCache.set(key, o);
            }
        }
    }
    _obsMapReceiptsLa = la;
    return _obsMapReceiptsCache;
}

// Account filter caches
let _accountFilterCache: Map<string, Model.LocalAuthorityChapterAccount[]> = new Map();
let _accountFilterLa: any = null;

function _getFilteredOperationAccounts(
    la: Model.LocalAuthority,
    receipts: boolean
): Model.LocalAuthorityChapterAccount[] {
    if (_accountFilterLa !== la) {
        _accountFilterCache = new Map();
        _accountFilterLa = la;
    }
    const key = `${receipts}`;
    if (_accountFilterCache.has(key)) return _accountFilterCache.get(key);
    const filtered = la.accounts.filter(
        (laca) =>
            laca.account.operation &&
            ((receipts && laca.account.receipts) ||
                (!receipts && laca.account.expense))
    );
    _accountFilterCache.set(key, filtered);
    return filtered;
}

// Order accounts cache
let _orderAccountsCache: Map<string, Model.LocalAuthorityChapterAccount[]> = new Map();
let _orderAccountsLa: any = null;

export function _aggregateRealOperations(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    receipts: boolean,
    previousScenario: Model.Scenario,
    priorities: number[],
    loans?: Model.PpiYearLoan[]
): number {
    let orderOperationsTotal = _aggregateOrderOperationsTotal(
        type,
        year,
        la,
        operations,
        receipts,
        receipts ? ["042", "043"] : ["042", "043", "023"],
        previousScenario,
        priorities
    );
    let operationsTotal = _aggregateOperationsTotal(
        type,
        year,
        la,
        operations,
        receipts,
        previousScenario,
        priorities,
        loans
    );
    return operationsTotal - orderOperationsTotal;
}

function _aggregateRealOperations1(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    receipts: boolean,
    previousScenario: Model.Scenario,
    priorities: number[],
    loans?: Model.PpiYearLoan[],
    totalInterestByYear?: any
): number {
    let orderOperationsTotal = _aggregateOrderOperationsTotal(
        type,
        year,
        la,
        operations,
        receipts,
        receipts ? ["042", "043"] : ["042", "043", "023"],
        previousScenario,
        priorities
    );
    let operationsTotal = _aggregateDispenseTotal(
        type,
        year,
        la,
        operations,
        receipts,
        previousScenario,
        priorities,
        loans,
        totalInterestByYear
    );
    return operationsTotal - orderOperationsTotal;
}

export function _aggregateCAFBrute(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    previousScenario: Model.Scenario,
    priorities: number[],
    loans: Model.PpiYearLoan[]
): number {
    const revenue = _aggregateOperationsTotal(
        type,
        year,
        la,
        operations,
        true,
        previousScenario,
        priorities
    );
    const expense = _aggregateOperationsTotal(
        type,
        year,
        la,
        operations,
        false,
        previousScenario,
        priorities,
        loans
    );

    let result = revenue - expense;
    result -= _aggregateOrderOperationsTotal(
        type,
        year,
        la,
        operations,
        true,
        null,
        previousScenario,
        priorities
    );
    result += _aggregateOrderOperationsTotal(
        type,
        year,
        la,
        operations,
        false,
        null,
        previousScenario,
        priorities
    );

    return result;
}

function _getOrderOperationAccounts(
    la: Model.LocalAuthority,
    receipts?: boolean,
    customNumbers?: string[]
): Model.LocalAuthorityChapterAccount[] {
    if (_orderAccountsLa !== la) {
        _orderAccountsCache = new Map();
        _orderAccountsLa = la;
    }
    const cacheKey = `${receipts}-${customNumbers ? customNumbers.join(',') : ''}`;
    if (_orderAccountsCache.has(cacheKey)) return _orderAccountsCache.get(cacheKey);

    let ok: boolean;
    let numbers = customNumbers ? customNumbers : orderNumbers;

    const result = la.accounts.filter((laca) => {
        ok = false;

        if (
            receipts == undefined ||
            receipts == null ||
            (receipts && laca.account.receipts) ||
            (!receipts && laca.account.expense)
        ) {
            for (const on of numbers) {
                if (ok) continue;

                if (on.indexOf("*") == -1) {
                    ok = laca.chapter && laca.chapter.number == on;
                } else {
                    ok = laca.account.number.startsWith(on.substring(0, on.length - 1));
                }
            }
        }

        return ok;
    });
    _orderAccountsCache.set(cacheKey, result);
    return result;
}

function _getLoansOperationAccounts(
    localAuthority: Model.LocalAuthority
): Model.LocalAuthorityChapterAccount[] {
    return localAuthority.accounts.filter(
        (laca) =>
            laca.account.number == "16" || laca.account.number.startsWith("1644")
    );
}

function _getLoansTotal(
    type: "retrospective" | "prospective",
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    previousScenario: Model.Scenario,
    priorities: number[]
): number {
    let result = 0;
    const obsMap = _getObsMap(la);

    for (const laca of _getLoansOperationAccounts(la)) {
        if (type == "retrospective") {
            const obs = obsMap.get(`${year}-${laca.account.id}`);
            if (obs) {
                result += obs.amount;
            }
        } else {
            result += operations.findValue(
                year,
                laca.account.id,
                laca.chapter ? laca.chapter.id : null,
                null,
                previousScenario,
                priorities
            );
        }
    }

    return result;
}

function _aggregateOrderOperationsTotal(
    type: "retrospective" | "prospective",
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    receipts: boolean,
    customNumbers: string[],
    previousScenario: Model.Scenario,
    priorities: number[]
): number {
    let result = 0;
    const obsMap = _getObsMap(la);

    for (const laca of _getOrderOperationAccounts(la, receipts, customNumbers)) {
        if (type == "retrospective") {
            const obs = obsMap.get(`${year}-${laca.account.id}`);
            if (obs) {
                result += obs.amount;
            }
        } else {
            result += operations.findValue(
                year,
                laca.account.id,
                laca.chapter ? laca.chapter.id : null,
                receipts,
                previousScenario,
                priorities
            );
        }
    }

    return result;
}

export function _aggregateDispenseTotal(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    receipts: boolean,
    previous_scenario: Model.Scenario,
    priorities: number[],
    loans?: Model.PpiYearLoan[],
    totalInterestByYear?: any
): number {
    let result = 0;

    const loan =
        !receipts && loans
            ? loans.find((loan) => loan.year === year)
            : { interests: 0 };

    let accounts: Model.LocalAuthorityChapterAccount[];
    if (receipts !== undefined && receipts !== null) {
        accounts = _getFilteredOperationAccounts(la, receipts);
    } else {
        accounts = la.accounts;
    }

    const obsMapR = _getObsMapReceipts(la);

    for (const laca of accounts) {
        if (type == "retrospective") {
            const obs = obsMapR.get(`${year}-${laca.account.id}-${receipts}`);
            if (obs && ((receipts && obs.account.receipts) || (!receipts && obs.account.expense))) {
                result += obs.amount;
            }
        } else {
            if ("66111" === laca.account.number) {
                result += totalInterestByYear;
            } else {
                result += operations.findValue(
                    year,
                    laca.account.id,
                    laca.chapter ? laca.chapter.id : null,
                    receipts,
                    previous_scenario,
                    priorities
                );
            }
        }
    }

    return result;
}

export function _aggregateOperationsTotal(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    receipts: boolean,
    previous_scenario: Model.Scenario,
    priorities: number[],
    loans?: Model.PpiYearLoan[]
): number {
    let result = 0;

    const loan =
        !receipts && loans
            ? loans.find((loan) => loan.year === year)
            : { interests: 0 };

    let accounts: Model.LocalAuthorityChapterAccount[];
    if (receipts !== undefined && receipts !== null) {
        accounts = _getFilteredOperationAccounts(la, receipts);
    } else {
        accounts = la.accounts;
    }

    const obsMapR = _getObsMapReceipts(la);

    for (const laca of accounts) {
        if (type == "retrospective") {
            const obs = obsMapR.get(`${year}-${laca.account.id}-${receipts}`);
            if (obs && ((receipts && obs.account.receipts) || (!receipts && obs.account.expense))) {
                result += obs.amount;
            }
        } else {
            if ("66111" === laca.account.number) {
                result += loan ? loan.interests : 0;
            } else {
                result += operations.findValue(
                    year,
                    laca.account.id,
                    laca.chapter ? laca.chapter.id : null,
                    receipts,
                    previous_scenario,
                    priorities
                );
            }
        }
    }

    return result;
}

export function _reduceResult(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    scenario: Model.Scenario,
    loans: Model.PpiYearLoan[],
    previousScenario: Model.Scenario,
    priorities: number[]
): number {
    const totalReceipts = _aggregateOperationsTotal(
        type,
        year,
        la,
        operations,
        true,
        previousScenario,
        priorities
    );
    const totalExpense = _aggregateOperationsTotal(
        type,
        year,
        la,
        operations,
        false,
        previousScenario,
        priorities,
        loans
    );
    return totalReceipts - totalExpense;
}

export function _reduceResult1(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    scenario: Model.Scenario,
    loans: Model.PpiYearLoan[],
    previousScenario: Model.Scenario,
    priorities: number[],
    totalInterestByYear: any
): number {
    const totalReceipts = _aggregateDispenseTotal(
        type,
        year,
        la,
        operations,
        true,
        previousScenario,
        priorities,
        loans,
        totalInterestByYear
    );
    const totalExpense = _aggregateDispenseTotal(
        type,
        year,
        la,
        operations,
        false,
        previousScenario,
        priorities,
        loans,
        totalInterestByYear
    );
    return totalReceipts - totalExpense;
}

function _reducePreviousResult(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    scenario: Model.Scenario,
    loans: Model.PpiYearLoan[],
    previousScenario: Model.Scenario,
    priorities: number[]
): number {
    let result = 0;

    if (type == "prospective" && year !== scenario.year) {
        if (year == scenario.year - 1) {
            if (previousScenario) {
                result = _reduceTotalResult(
                    type,
                    year,
                    la,
                    operations,
                    previousScenario,
                    loans,
                    priorities
                );
            }
        } else {
            const scenarioYear = scenario.years.find((y) => y.year == year);
            if (scenarioYear && scenarioYear.previous) {
                result = scenarioYear.previous;
            }
        }
    } else {
        if (year == scenario.year) {
            const scenarioYear = scenario.years.find((y) => y.year == year);
            if (scenarioYear && scenarioYear.previous) {
                result = scenarioYear.previous;
            }
        } else {
            const laResult = la.results.find((r) => r.year == year - 1 && r.operation);
            result = laResult ? laResult.amount : 0;
            if (!result) result = 0;
        }
    }

    return result;
}

function _reduceTotalResult(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    scenario: Model.Scenario,
    loans: Model.PpiYearLoan[],
    priorities: number[],
    previousScenario?: Model.Scenario
): number {
    const result = _reduceResult(
        type,
        year,
        la,
        operations,
        scenario,
        loans,
        previousScenario,
        priorities
    );
    const previousResult = _reducePreviousResult(
        type,
        year,
        la,
        operations,
        scenario,
        loans,
        previousScenario,
        priorities
    );
    return result + previousResult;
}

function _reduceTotalResult1(
    type: OperationType,
    year: number,
    la: Model.LocalAuthority,
    operations: Operations,
    scenario: Model.Scenario,
    loans: Model.PpiYearLoan[],
    priorities: number[],
    previousScenario?: Model.Scenario,
    propsloans?: any
): number {
    const totalInterestByYear = LoanDomain.getTotalInterestByYear(
        propsloans,
        year
    );
    const result = _reduceResult1(
        type,
        year,
        la,
        operations,
        scenario,
        loans,
        previousScenario,
        priorities,
        totalInterestByYear
    );
    const previousResult = _reducePreviousResult(
        type,
        year,
        la,
        operations,
        scenario,
        loans,
        previousScenario,
        priorities
    );
    return result + previousResult;
}

export default (
    scenario: Model.Scenario,
    type: TabId,
    localAuthority: Model.LocalAuthority,
    shownChapters: number[],
    priorities: number[]
) =>
    (() => {
        let rows: Row[] = [],
            receiptsCount = 0,
            dico: DicoChapter[] = [],
            currentChapter: Model.Chapter;

        const defaultReceiptsChapter = {
            label: "Non chapitrées",
            id: -1,
            number: "0",
            receipts: true,
        } as DicoChapter;
        const defaultExpensesChapter = {
            label: "Non chapitrées",
            id: -2,
            number: "0",
            receipts: false,
        } as DicoChapter;

        const receipts = type == "revenues";
        let accounts: Model.LocalAuthorityChapterAccount[] = [];
        localAuthority.accounts.map((laca) => {
            if (laca.account.receipts == laca.account.expense) {
                accounts.push({
                    ...laca,
                    account: { ...laca.account, receipts: true, expense: false },
                });
                accounts.push({
                    ...laca,
                    account: { ...laca.account, receipts: false, expense: true },
                });
            } else {
                accounts.push(laca);
            }
        });

        accounts = accounts.sort((a, b) =>
            a.account.number.toString() > b.account.number.toString() ? 1 : -1
        );

        accounts
            .filter((a) =>
                type == "synthesis"
                    ? a.account.operation
                    : a.account.operation &&
                    ((receipts && a.account.receipts) ||
                        (!receipts && a.account.expense))
            )
            .map((account) => {
                currentChapter = account.chapter;
                if (!currentChapter) {
                    currentChapter = account.account.receipts
                        ? defaultReceiptsChapter
                        : defaultExpensesChapter;
                }

                let dicoChapter: DicoChapter = null;
                if (
                    !(dicoChapter = dico.find(
                        (c) =>
                            c.id == currentChapter.id &&
                            c.receipts == account.account.receipts
                    ))
                ) {
                    dicoChapter = currentChapter as DicoChapter;
                    dicoChapter.accounts = [];
                    dicoChapter.receipts = account.account.receipts;
                    dico.push(dicoChapter);

                    if (account.account.receipts) receiptsCount++;
                }

                dicoChapter.accounts.push(account.account);
            });

        dico = dico.sort((a, b) => {
            if (a.receipts) {
                if (b.receipts) {
                    if (a.number == "042") return 1;
                    else if (b.number == "042") return -1;
                    else return a.number.toString() > b.number.toString() ? 1 : -1;
                } else return -1;
            } else {
                if (!b.receipts) {
                    if (a.number == "042") return 1;
                    else if (b.number == "042") return -1;
                    else return a.number.toString() > b.number.toString() ? 1 : -1;
                } else return 1;
            }
        });

        let index = 0;

        if (type == "synthesis") {
            rows.push({
                title: "Recettes de fonctionnement",
                className: "first-header",
                readOnly: true,
                index,
            });
            index++;
        }

        dico.map((chapter) => {
            let scenarioChapter =
                scenario &&
                scenario.chapters &&
                scenario.chapters.find(
                    (c) =>
                        c.chapter_id == (chapter.id == -1 ? null : chapter.id) &&
                        c.receipts == receipts
                );
            rows.push({
                scenario,
                chapter,
                title: chapter.label,
                readOnly: true,
                isShown: shownChapters && shownChapters.indexOf(chapter.id) !== -1,
                className: "scenario-chapter",
                amount: scenarioChapter ? scenarioChapter.amount : null,
                comment: scenarioChapter ? scenarioChapter.comment : null,
                cellProps: amountCellProps,
                cellComponents:
                    type !== "synthesis"
                        ? [
                            {
                                index: 0,
                                component: ChapterHeader,
                            },
                        ]
                        : null,
                index,
            });
            index++;

            if (type == "synthesis") {
                if (receiptsCount == index - 1) {
                    rows.push({
                        name: "REAL_RECEIPTS",
                        title: "Recettes réelles de fonctionnement",
                        cellProps: amountCellProps,
                        readOnly: true,
                        className: "cat-footer",
                        func: (
                            type,
                            year,
                            la,
                            operations,
                            scenario,
                            loans,
                            previousScenario
                        ) => {
                            const result = _aggregateRealOperations(
                                type,
                                year,
                                la,
                                operations,
                                true,
                                previousScenario,
                                priorities
                            );
                            return result || null;
                        },
                        index,
                    });
                    index++;
                    rows.push({
                        title: "Recettes Total des opérations d'ordre",
                        cellProps: amountCellProps,
                        readOnly: true,
                        className: "cat-footer",
                        func: (
                            type,
                            year,
                            la,
                            operations,
                            scenario,
                            loans,
                            previous_scenario
                        ) => {
                            const result = _aggregateOrderOperationsTotal(
                                type,
                                year,
                                la,
                                operations,
                                true,
                                ["042", "043"],
                                previous_scenario,
                                priorities
                            );
                            return result || null;
                        },
                        index,
                    });
                    index++;
                    rows.push({
                        title: "Total des recettes",
                        cellProps: amountCellProps,
                        className: "ppi-total",
                        readOnly: true,
                        func: (
                            type,
                            year,
                            la,
                            operations,
                            scenario,
                            loans,
                            previous_scenario
                        ) => {
                            const result = _aggregateOperationsTotal(
                                type,
                                year,
                                la,
                                operations,
                                true,
                                previous_scenario,
                                priorities
                            );
                            return result || null;
                        },
                        index,
                    });
                    index++;
                    rows.push({
                        title: "Dépenses de fonctionnement",
                        className: "first-header",
                        readOnly: true,
                        index,
                    });
                    index++;
                }
            } else {
                if (shownChapters.indexOf(chapter.id) !== -1) {
                    chapter.accounts.map((account) => {
                        const scenarioAccount =
                            scenario &&
                            scenario.accounts &&
                            scenario.accounts.find(
                                (a) => a.account_id == account.id && a.receipts == receipts
                            );
                        rows.push({
                            scenario,
                            account,
                            chapter,
                            title: account.label,
                            type,
                            readOnly: true,
                            amount: scenarioAccount ? scenarioAccount.amount : null,
                            comment: scenarioAccount ? scenarioAccount.comment : null,
                            cellProps: amountCellProps,
                            cellComponents: [
                                {
                                    index: 0,
                                    component: AccountHeader,
                                },
                                {
                                    indices: [7, 8, 9, 10, 11, 12],
                                    component: AccountYearEditor,
                                },
                            ],
                            index,
                        });
                        index++;
                    });
                }
            }
        });

        if (type == "synthesis") {
            rows.push({
                name: "REAL_EXPENSES",
                title: "Dépenses réelles de fonctionnement",
                cellProps: amountCellProps,
                readOnly: true,
                className: "cat-footer",
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previous_scenario,
                    propsloans
                ) => {
                    const totalInterestByYear = LoanDomain.getTotalInterestByYear(
                        propsloans,
                        year
                    );
                    const result = _aggregateRealOperations1(
                        type,
                        year,
                        la,
                        operations,
                        false,
                        previous_scenario,
                        priorities,
                        loans,
                        totalInterestByYear
                    );
                    return result || null;
                },
                index,
            });
            index++;
            rows.push({
                title: "Dépenses Total des opérations d'ordre",
                cellProps: amountCellProps,
                readOnly: true,
                className: "cat-footer",
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previous_scenario
                ) => {
                    const result = _aggregateOrderOperationsTotal(
                        type,
                        year,
                        la,
                        operations,
                        false,
                        ["042", "043", "023"],
                        previous_scenario,
                        priorities
                    );
                    return result || null;
                },
                index,
            });
            index++;
            rows.push({
                title: "Total des dépenses",
                cellProps: amountCellProps,
                readOnly: true,
                className: "ppi-total",
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario,
                    propsloans
                ) => {
                    const totalInterestByYear = LoanDomain.getTotalInterestByYear(
                        propsloans,
                        year
                    );
                    const result = _aggregateDispenseTotal(
                        type,
                        year,
                        la,
                        operations,
                        false,
                        previousScenario,
                        priorities,
                        loans,
                        totalInterestByYear
                    );
                    return result || null;
                },
                index,
            });
            index++;
        }

        if (type == "synthesis") {
            rows.push({
                title: "Résultat",
                readOnly: true,
                className: "first-header",
                cellProps: amountCellProps,
                index,
            });
            index++;
            rows.push({
                key: "RESULT",
                title: "Résultat de l'année",
                readOnly: true,
                cellProps: amountCellProps,
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario,
                    propsloans
                ) => {
                    const totalInterestByYear = LoanDomain.getTotalInterestByYear(
                        propsloans,
                        year
                    );
                    const result = _reduceResult1(
                        type,
                        year,
                        la,
                        operations,
                        scenario,
                        loans,
                        null,
                        [],
                        totalInterestByYear
                    );
                    return result || null;
                },
                index,
            });
            index++;
            rows.push({
                key: "PREVIOUS_RESULT",
                title: "Résultat de l'année précédente",
                readOnly: false,
                disabledCells: [0, 1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12],
                cellProps: amountCellProps,
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario
                ) => {
                    const result = _reducePreviousResult(
                        type,
                        year,
                        la,
                        operations,
                        scenario,
                        loans,
                        previousScenario,
                        priorities
                    );
                    return result || null;
                },
                index,
            });
            index++;
            rows.push({
                key: "TOTAL_RESULT",
                title: "Résultat total",
                readOnly: true,
                cellProps: amountCellProps,
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario,
                    propsloans
                ) => {
                    const result = _reduceTotalResult1(
                        type,
                        year,
                        la,
                        operations,
                        scenario,
                        loans,
                        priorities,
                        previousScenario,
                        propsloans
                    );
                    return result || null;
                },
                index,
            });
            index++;
            rows.push({
                title: "Ratios",
                cellProps: amountCellProps,
                className: "first-header",
                readOnly: true,
                index,
            });
            index++;

            rows.push({
                title: "C.A.F Brute",
                readOnly: true,
                className: "cat-footer",
                cellProps: amountCellProps,
                index,
            });
            index++;

            _getOrderOperationAccounts(localAuthority).map((laca) => {
                rows.push({
                    title: laca.account.number + " - " + laca.account.label,
                    readOnly: true,
                    cellProps: amountCellProps,
                    mayBeHidden: true,
                    className: "scenario-ratios-row",
                    func: (
                        type,
                        year,
                        la,
                        operations,
                        scenario,
                        loans,
                        previous_scenario
                    ) => {
                        let result = 0,
                            obs: Model.Observation;

                        if (type == "retrospective") {
                            if (
                                (obs = la.observations.find(
                                    (o) => o.year == year && o.account.id == laca.account.id
                                ))
                            ) {
                                result = obs.amount;
                            }
                        } else {
                            result = operations.findValue(
                                year,
                                laca.account.id,
                                laca.chapter ? laca.chapter.id : null,
                                null,
                                previous_scenario,
                                priorities
                            );
                        }
                        return result;
                    },
                    index,
                });
                index++;
            });

            rows.push({
                name: "CAFB",
                title: "Capacité d'autofinancement brute",
                cellProps: amountCellProps,
                readOnly: true,
                className: "ppi-total",
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario
                ) => {
                    const result = _aggregateCAFBrute(
                        type,
                        year,
                        la,
                        operations,
                        previousScenario,
                        priorities,
                        loans
                    );
                    return result || null;
                },
                index,
            });
            index++;

            rows.push({
                title: "C.A.F Nette",
                readOnly: true,
                className: "cat-footer",
                cellProps: amountCellProps,
                index,
            });
            index++;

            rows.push({
                name: "LOANS",
                title: "Remboursement capital emprunts",
                readOnly: true,
                cellProps: amountCellProps,
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previous_scenario
                ) => {
                    let result = 0;
                    if (loans) {
                        let loan = loans.find((l) => l.year == year);
                        if (loan) {
                            result = loan.reimbours;
                        }
                    }
                    return result || null;
                },
                index,
            });
            index++;

            rows.push({
                name: "CAFN",
                title: "Capacité d'autofinancement nette",
                className: "ppi-total",
                cellProps: amountCellProps,
                readOnly: true,
                func: (
                    type,
                    year,
                    la,
                    operations,
                    scenario,
                    loans,
                    previousScenario
                ) => {
                    let result = 0;
                    if (loans) {
                        let loan = loans.find((l) => l.year == year);
                        if (loan) {
                            result = loan.reimbours;
                        }
                    }

                    result =
                        _aggregateCAFBrute(
                            type,
                            year,
                            la,
                            operations,
                            previousScenario,
                            priorities,
                            loans
                        ) - result;

                    return result || null;
                },
                index,
            });
            index++;
        } else {
            rows.push({
                title: "Total",
                readOnly: true,
                cellProps: amountCellProps,
                className: "cat-footer",
                index,
            });
            index++;
        }

        return { rows };
    })();

export interface CellProps {
    format: string;
    language: string;
    mantissa: number;
    type: "numeric";
}

export interface DicoChapter extends Model.Chapter {
    accounts: Model.Account[];
    receipts: boolean;
}

export interface MultiCellComponent extends BaseCellComponent {
    indices: number[];
}

export interface Row {
    account?: Model.Account;
    amount?: number | null;
    cellComponents?: CellComponent[] | null;
    cellProps?: CellProps;
    chapter?: DicoChapter;
    className?: string;
    disabledCells?: number[];
    editableHeader?: boolean;
    func?: RowFunction;
    index: number;
    isShown?: boolean;
    key?: RowKey;
    mayBeHidden?: boolean;
    name?: string;
    readOnly: boolean;
    scenario?: Model.Scenario;
    title: string;
    type?: TabId;
    comment?: string | null;
}

export interface RowFunction {
    (
        type: OperationType,
        year: number,
        localAuthority: Model.LocalAuthority,
        operations: Operations,
        scenario: Model.Scenario,
        loans: Model.PpiYearLoan[],
        previousScenario: Model.Scenario,
        propsloans: any
    ): number | null;
}

export interface SingleCellComponent extends BaseCellComponent {
    index: number;
}

export type CellComponent = MultiCellComponent | SingleCellComponent;
export type OperationType = "prospective" | "retrospective";
export type RowKey = "PREVIOUS_RESULT" | "RESULT" | "TOTAL_RESULT";
export type TabId = "revenues" | "spending" | "synthesis";

interface BaseCellComponent {
    component: React.ComponentClass<any>;
}



// WEBPACK FOOTER //
// ./src/component/scenarii/detail/table-definition.ts